import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileWarning, RefreshCw, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function MissingSalesLog() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lookbackDays, setLookbackDays] = useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data }, { data: preferences }] = await Promise.all([
        api.get("/missing-sales/log"),
        api.get("/missing-sales/log/preferences"),
      ]);
      const nextRecords = Array.isArray(data)
        ? data
        : data?.records || data?.items || data?.log || [];
      setRecords(nextRecords);
      setLookbackDays(Number(preferences?.lookback_days) || 90);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  const saveLookback = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/missing-sales/log/preferences", {
        lookback_days: lookbackDays,
      });
      setLookbackDays(Number(data?.lookback_days) || lookbackDays);
      const { data: logData } = await api.get("/missing-sales/log");
      setRecords(Array.isArray(logData)
        ? logData
        : logData?.records || logData?.items || logData?.log || []);
      toast.success(t("missingSales.log.rangeSaved"));
    } catch (requestError) {
      toast.error(formatApiError(requestError.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const sortedRecords = useMemo(
    () => [...records].sort((a, b) => generatedName(a).localeCompare(generatedName(b))),
    [records]
  );

  if (loading) {
    return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;
  }

  if (error) {
    return (
      <div className="pg-card p-8 text-center" data-testid="missing-sales-log-error">
        <h1 className="text-xl font-bold text-slate-900">{t("missingSales.log.loadFailed")}</h1>
        <p className="text-sm text-slate-500 mt-2">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-6 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm inline-flex items-center gap-2"
        >
          <RefreshCw size={15} /> {t("common.refresh")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="missing-sales-log-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("missingSales.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">
            {t("missingSales.log.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            {t("missingSales.log.description")}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold text-sm flex items-center gap-2"
          data-testid="refresh-missing-sales-log"
        >
          <RefreshCw size={15} /> {t("common.refresh")}
        </button>
      </div>

      <section className="pg-card p-5 mb-5" data-testid="missing-sales-lookback-settings">
        <div className="flex flex-col lg:flex-row lg:items-end gap-5">
          <div className="flex-1">
            <label htmlFor="missing-sales-lookback" className="block font-bold text-slate-900 mb-1">
              {t("missingSales.log.rangeTitle", { count: lookbackDays })}
            </label>
            <p className="text-xs text-slate-500 mb-4">{t("missingSales.log.rangeDescription")}</p>
            <div className="flex items-center gap-4">
              <input
                id="missing-sales-lookback"
                type="range"
                min="1"
                max="90"
                step="1"
                value={lookbackDays}
                onChange={(event) => setLookbackDays(Number(event.target.value))}
                className="w-full accent-blue-600"
                data-testid="missing-sales-lookback-slider"
              />
              <input
                type="number"
                min="1"
                max="90"
                value={lookbackDays}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  if (Number.isFinite(value)) setLookbackDays(Math.min(90, Math.max(1, value)));
                }}
                aria-label={t("missingSales.log.daysInput")}
                className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-center font-bold text-slate-800"
              />
            </div>
            <div className="flex justify-between text-[11px] text-slate-400 mt-1">
              <span>1</span><span>90</span>
            </div>
          </div>
          <button
            type="button"
            onClick={saveLookback}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
            data-testid="save-missing-sales-lookback"
          >
            <Save size={15} /> {saving ? t("missingSales.log.savingRange") : t("missingSales.log.saveRange")}
          </button>
        </div>
      </section>

      <section className="pg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
              <FileWarning size={18} />
            </div>
            <div>
              <h2 className="font-bold text-slate-900">{t("missingSales.log.records")}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t("missingSales.log.monitoredOnly")}</p>
            </div>
          </div>
          <span className="pill pill-danger">{sortedRecords.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
                <th className="px-5 py-3 font-semibold">{t("missingSales.log.columns.store")}</th>
                <th className="px-5 py-3 font-semibold">{t("missingSales.log.columns.generated")}</th>
                <th className="px-5 py-3 font-semibold">{t("missingSales.log.columns.status")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-5 py-12 text-center text-slate-400">
                    <AlertTriangle className="mx-auto mb-2 text-slate-300" size={32} />
                    {t("missingSales.log.empty")}
                  </td>
                </tr>
              ) : (
                sortedRecords.map((record, index) => (
                  <tr
                    key={record.id || `${record.store_id || record.store_name}-${generatedName(record)}-${index}`}
                    className="border-t border-slate-100 hover:bg-slate-50/50"
                    data-testid={`missing-sales-log-row-${record.id || index}`}
                  >
                    <td className="px-5 py-4 font-semibold text-slate-800">
                      {record.store_name || record.store?.name || "—"}
                    </td>
                    <td className="px-5 py-4 mono text-xs text-slate-700 break-all">
                      {generatedName(record)}
                    </td>
                    <td className="px-5 py-4">
                      <span className="pill pill-danger">{t("missingSales.status")}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function generatedName(record) {
  return record.generated_filename || record.generated || record.filename || "—";
}
